import { useContext, useEffect, useState } from 'react';
import { DaoContext, useSnapshotFunctions } from './daoHook';
import moment from 'moment';
import { DaoRedirectBadge } from './daoComponents/redirectionBadge';
import { toast } from 'react-hot-toast';
import MarkdownEditor, { mdRender } from '../../markdown';

const Label = ({ label }) => {
  return (
    <span className='text-base font-semibold text-jacarta-700 dark:text-jacarta-200'>{label}</span>
  );
};

const Title = ({ title, description }) => {
  return (
    <div className='flex h-auto w-full flex-col space-y-2 pb-2 md:w-3/12 md:border-b-[1px] md:border-b-jacarta-300/30'>
      <span className='text-base font-semibold text-accent-dark dark:text-accent'>{title}</span>
      <span className='w-48 text-xs font-light text-jacarta-900 dark:text-jacarta-200'>
        {description}
      </span>
    </div>
  );
};

const DaoRequestContainer = ({ title, description, children }) => {
  return (
    <div className='h-fit w-full flex-row gap-12 md:flex'>
      <Title title={title} description={description} />
      <div className='flex flex-col space-y-8 pb-4 md:w-9/12 md:max-w-[552px]'>{children}</div>
    </div>
  );
};

export const Input = ({ value, setValue, required, maxLength }) => {
  return (
    <input
      className={`h-[48px] w-full rounded-[12px] border-[1px] border-solid border-[#898b8e80] px-3 py-4 text-sm font-normal outline-none focus:border-accent/100 dark:bg-jacarta-700 dark:text-white dark:focus:border-accent-dark/100`}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      required={required}
      maxLength={maxLength}
    />
  );
};

export const MakeDaoRequest = () => {
  const { daoData } = useContext(DaoContext);
  const { votingPower, space } = daoData;
  const [blockNumber, setBlockNumber] = useState('');
  const { backToProposals, getBlockNumber } = useSnapshotFunctions();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DDThh:mm:ss'));
  const [toDate, setToDate] = useState(
    moment()
      .add(3 * 24 * 60 * 60 * 1000)
      .hours(23)
      .minutes(59)
      .seconds(59)
      .format('YYYY-MM-DDThh:mm:ss'),
  );
  const [choices, setChoices] = useState(
    Array(2)
      .fill({})
      .map((v, i) => {
        return {
          id: i,
          description: '',
        };
      }),
  );

  useEffect(() => {
    getBlockNumber().then((blockNumber) => {
      setBlockNumber(blockNumber);
    });
  }, []);

  const { createProposal } = useSnapshotFunctions();

  const createProposalHandler = async () => {
    await createProposal(
      title,
      description,
      choices.map((d) => d.description),
      Math.floor(new Date(fromDate).getTime() / 1000),
      Math.floor(new Date(toDate).getTime() / 1000),
    );
  };

  const addChoice = () => {
    setChoices([
      ...choices,
      {
        id: choices.length,
        description: '',
      },
    ]);
  };

  const removeChoice = (id) => {
    setChoices(
      choices
        .filter((c) => c?.id !== id)
        .map((c, i) => {
          return {
            ...c,
            id: i,
          };
        }),
    );
    // setChoices(choices.filter((c) => c?.id !== id));
  };

  const vpNotEnoughToCreateProposal =
    votingPower < space?.validation?.params?.minScore ||
    !space ||
    space?.validation?.name !== 'basic';
  const requiredVotingPower = space?.validation?.params?.minScore;

  return (
    <>
      <button
        className='flex rounded-r-full bg-[#E7E8EC] px-[12px] py-[6px] text-[12px] text-[#666] dark:bg-jacarta-600 dark:text-white'
        onClick={backToProposals}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='9'
          height='14'
          viewBox='0 0 9 14'
          fill='none'
          className='mr-3 mt-[2px]'
        >
          <path
            d='M8.41 11.59L3.83 7L8.41 2.41L7 1L1 7L7 13L8.41 11.59Z'
            fill='#666666'
            className='dark:fill-white'
            stroke='#E7E8EC'
            strokeWidth='0.5'
          />
        </svg>
        Back to Votes
      </button>
      <div className='mt-10 flex h-full flex-col'>
        <div className='flex flex-col justify-center'>
          <div className='text-xl font-semibold text-jacarta-700 dark:text-jacarta-100'>
            Make a Proposal
          </div>
        </div>
        {vpNotEnoughToCreateProposal && (
          <div className='alert-error mt-3 flex rounded-[12px] bg-[#E7E8EC] px-[12px] py-[6px] text-[#7445FF]'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='32'
              height='32'
              viewBox='0 0 32 32'
              fill='none'
            >
              <circle cx='16' cy='16' r='16' fill='#7445FF' />
              <path
                d='M17.4239 8L17.0652 18.9891H14.4565L14.087 8H17.4239ZM14 22.3913C14 21.9275 14.1594 21.5435 14.4783 21.2391C14.8043 20.9275 15.2319 20.7717 15.7609 20.7717C16.2971 20.7717 16.7246 20.9275 17.0435 21.2391C17.3623 21.5435 17.5217 21.9275 17.5217 22.3913C17.5217 22.8406 17.3623 23.221 17.0435 23.5326C16.7246 23.8442 16.2971 24 15.7609 24C15.2319 24 14.8043 23.8442 14.4783 23.5326C14.1594 23.221 14 22.8406 14 22.3913Z'
                fill='white'
              />
            </svg>
            <span className='ml-2 mt-1'>
              You need at least {requiredVotingPower} Nfts of voting power to create a proposal.
            </span>
          </div>
        )}
        <div className='mt-10 flex h-full flex-col space-y-6'>
          <DaoRequestContainer title='Description' description='Write content in Markdown!'>
            <div className='flex w-full flex-col space-y-3'>
              <Label label='Title' />
              <Input value={title} setValue={setTitle} />
            </div>

            <div className='flex w-full flex-col space-y-3'>
              <Label label='Content' />
              <div data-color-mode='dark' className='dark:invert'>
                <MarkdownEditor value={description} onChange={(v) => setDescription(v)} />
              </div>
            </div>

            <div className='flex w-full flex-col space-y-3'>
              <div className='w-full overflow-hidden rounded-[12px] border border-[#898b8e4e] dark:border-[#898b8e80]'>
                <div className='bg-[whitesmoke] px-[12px] py-[6px] text-[#aaa] dark:bg-jacarta-600 dark:text-jacarta-100'>
                  Preview
                </div>
                <div
                  className='custom-html-style min-h-[100px] bg-white p-[16px] dark:bg-jacarta-800 dark:text-jacarta-300'
                  dangerouslySetInnerHTML={{ __html: mdRender(description) }}
                />
              </div>
            </div>
          </DaoRequestContainer>

          <DaoRequestContainer title='Choices'>
            <div className='flex w-full flex-col space-y-4'>
              {choices.map((choice) => {
                return (
                  <div key={choice?.id} className='flex w-full flex-row space-x-3'>
                    <span className='flex items-center text-base font-semibold text-jacarta-900 dark:text-jacarta-200'>
                      {choice?.id + 1}
                    </span>
                    <Input
                      value={choice?.description}
                      setValue={(v) => {
                        setChoices(
                          choices.map((c) => {
                            if (c?.id === choice?.id) {
                              return {
                                ...c,
                                description: v,
                              };
                            }
                            return c;
                          }),
                        );
                      }}
                    />
                    {choice?.id > 1 && (
                      <ChoiceButton
                        icon={<ButtonIcon />}
                        onClick={() => removeChoice(choice?.id)}
                      />
                    )}
                  </div>
                );
              })}
              <ChoiceButton icon={<ButtonIcon plus />} onClick={addChoice} wFull />
            </div>
          </DaoRequestContainer>

          <DaoRequestContainer title='Date&Time'>
            <div className='w-full flex-row md:flex md:space-x-3'>
              <div className='mt-2 block md:w-1/2'>
                <span className='block w-full text-base font-semibold text-jacarta-700 dark:text-jacarta-200'>
                  Starts*
                </span>
                <input
                  type='datetime-local'
                  min={moment().format('YYYY-MM-DDThh:mm:ss')}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                  }}
                  className='mt-3 rounded-[12px] border border-solid border-jacarta-400/30 focus:border-accent/100 dark:border-jacarta-400/40 dark:bg-jacarta-700 dark:text-white dark:focus:border-accent-dark/100'
                />
              </div>
              <div className='w-1/ mt-2'>
                <span className='block w-full text-base font-semibold text-jacarta-700 dark:text-jacarta-200'>
                  Ends*
                </span>
                <input
                  type='datetime-local'
                  min={moment().format('YYYY-MM-DDThh:mm:ss')}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className='mt-3 rounded-[12px] border border-solid border-jacarta-400/30 focus:border-accent/100 dark:border-jacarta-400/40 dark:bg-jacarta-700 dark:text-white dark:focus:border-accent-dark/100'
                />
              </div>
            </div>
          </DaoRequestContainer>

          <DaoRequestContainer title='Identity'>
            <div className='flex w-full flex-row space-x-6'>
              {/*<Badge
                name='Identifier'
                text='0x8B9B24fDB8b7caEF4fF6C3107f047462896A7611'
                isAddress
            />*/}
              {/*<Badge name='Snapshot' text={blockNumber} />*/}
              <DaoRedirectBadge
                name={'Snapshot'}
                text={blockNumber}
                isToScan
                redirectUrl={`https://bscscan.com/block/${blockNumber}`}
              />
            </div>
          </DaoRequestContainer>

          <div className='flex h-fit w-full flex-row gap-12'>
            <div className='hidden h-auto w-full flex-col space-y-2 pb-2 md:flex md:w-3/12' />
            <div className='w-full'>
              <PublishButton
                onClick={createProposalHandler}
                disabled={vpNotEnoughToCreateProposal || description.length > 20 * 1000}
              />
              {description.length > 20 * 1000 && (
                <div className='alert-error mt-3 flex rounded-[12px] bg-[#E7E8EC] px-[12px] py-[6px] text-[#7445FF]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='32'
                    height='32'
                    viewBox='0 0 32 32'
                    fill='none'
                  >
                    <circle cx='16' cy='16' r='16' fill='#7445FF' />
                    <path
                      d='M17.4239 8L17.0652 18.9891H14.4565L14.087 8H17.4239ZM14 22.3913C14 21.9275 14.1594 21.5435 14.4783 21.2391C14.8043 20.9275 15.2319 20.7717 15.7609 20.7717C16.2971 20.7717 16.7246 20.9275 17.0435 21.2391C17.3623 21.5435 17.5217 21.9275 17.5217 22.3913C17.5217 22.8406 17.3623 23.221 17.0435 23.5326C16.7246 23.8442 16.2971 24 15.7609 24C15.2319 24 14.8043 23.8442 14.4783 23.5326C14.1594 23.221 14 22.8406 14 22.3913Z'
                      fill='white'
                    />
                  </svg>
                  <span className='ml-2 mt-1'>Description is too long. Max 20,000 characters.</span>
                </div>
              )}
              {vpNotEnoughToCreateProposal && (
                <div className='alert-error mt-3 flex rounded-[12px] bg-[#E7E8EC] px-[12px] py-[6px] text-[#7445FF]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='32'
                    height='32'
                    viewBox='0 0 32 32'
                    fill='none'
                  >
                    <circle cx='16' cy='16' r='16' fill='#7445FF' />
                    <path
                      d='M17.4239 8L17.0652 18.9891H14.4565L14.087 8H17.4239ZM14 22.3913C14 21.9275 14.1594 21.5435 14.4783 21.2391C14.8043 20.9275 15.2319 20.7717 15.7609 20.7717C16.2971 20.7717 16.7246 20.9275 17.0435 21.2391C17.3623 21.5435 17.5217 21.9275 17.5217 22.3913C17.5217 22.8406 17.3623 23.221 17.0435 23.5326C16.7246 23.8442 16.2971 24 15.7609 24C15.2319 24 14.8043 23.8442 14.4783 23.5326C14.1594 23.221 14 22.8406 14 22.3913Z'
                      fill='white'
                    />
                  </svg>
                  <span className='ml-2 mt-1'>
                    You need at least {requiredVotingPower} Nfts of voting power to create a
                    proposal.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

//prettier-ignore
export const ChoiceButton = ({ icon, onClick, wFull = false }) => {
  return (
    <div className={`flex items-center justify-center ${wFull ? 'w-full' : ''}`}>
      <div className='relative h-6 w-6 cursor-pointer' onClick={onClick}>
        <div className='absolute left-0 right-0 h-6 w-6 rounded-full bg-[#E7E8EC]' />
        <div className='absolute left-1.5 top-1.5 h-3 w-3'>{icon}</div>
        {/* <div className='absolute left-1.5 top-1.5 h-3 w-3'>
        <div
          style={{
            border: '0.50px #7445FF solid',
          }}
          className='absolute left-0 top-1.5 h-0 w-3'
        />
        <div
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: '0 0',
            border: '0.50px #7445FF solid',
          }}
          className='absolute left-1.5 top-3 h-0 w-3 -rotate-90 transform'
        />
      </div> */}
      </div>
    </div>
  );
};

const PublishButton = ({ onClick, disabled }) => {
  const [buttonText, setButtonText] = useState('Publish Request');
  return (
    <div
      onClick={async () => {
        if (disabled) return;
        setButtonText('Publishing...');
        try {
          await onClick();
        } catch (e) {
          setButtonText('Publish Request');
          return toast.error("Couldn't create proposal.");
        }
        toast.success('Proposal created successfully');
        setButtonText('Publish Request');
      }}
      className={
        disabled
          ? 'mt-7 rounded-[12px] bg-[#E7E8EC] px-[16px] py-[12px] text-[14px] text-[#7D7F94] dark:cursor-not-allowed dark:bg-[#2B2E37] dark:text-[#7D7F94]'
          : 'mt-7 cursor-pointer rounded-[12px] bg-[#7445FF] px-[16px] py-[12px] text-[14px] text-[#FFF] dark:bg-[#7445FF] dark:text-[#FFF]'
      }
    >
      <div className='text-center text-base font-medium text-white'>{buttonText}</div>
    </div>
  );
};

//prettier-ignore
export const ButtonIcon = ({ plus }) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
      {plus ? (
        <path
          d='M6 1V11'
          stroke='#7445FF'
          strokeWidth='1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      ) : null}
      <path
        d='M1 6H11'
        stroke='#7445FF'
        strokeWidth='1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};
